// webauthn-vault.js — v4.9.2 : déverrouillage local du coffre via WebAuthn PRF.
//
// Important : ceci n'authentifie PAS le compte auprès du serveur. Le navigateur
// demande au mécanisme système (Face ID / Touch ID sur Apple, empreinte ou authentification système sur Android, Windows Hello, etc.)
// d'évaluer une PRF liée à une credential WebAuthn. Cette valeur sert uniquement
// à déchiffrer une seconde enveloppe de la DEK du coffre, localement.
//
// Aucun résultat biométrique, secret PRF ou clé du coffre n'est envoyé au VPS.

function bytesToB64url(bytes) {
  let binary = '';
  const raw = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes || 0);
  for (const b of raw) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function b64urlToBytes(value) {
  const s = String(value || '').replace(/-/g, '+').replace(/_/g, '/');
  const padded = s + '='.repeat((4 - (s.length % 4)) % 4);
  const binary = atob(padded);
  return Uint8Array.from(binary, (c) => c.charCodeAt(0));
}

function randomBytes(size = 32) {
  const out = new Uint8Array(size);
  crypto.getRandomValues(out);
  return out;
}

export function webAuthnAvailable() {
  return typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && !!navigator?.credentials?.create
    && !!navigator?.credentials?.get
    && window.isSecureContext === true;
}

export async function platformAuthenticatorAvailable() {
  if (!webAuthnAvailable()) return false;
  try {
    if (typeof PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable !== 'function') return true;
    return !!(await PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable());
  } catch (_) { return false; }
}

function prfResult(credential) {
  try {
    const ext = credential?.getClientExtensionResults?.();
    const first = ext?.prf?.results?.first;
    return first ? new Uint8Array(first) : null;
  } catch (_) { return null; }
}

async function evaluateCredentialPrf(credentialId, prfSalt) {
  const idBytes = b64urlToBytes(credentialId);
  const saltBytes = b64urlToBytes(prfSalt);
  const evalByCredential = {
    [credentialId]: { first: saltBytes },
  };
  const base = {
    challenge: randomBytes(32),
    timeout: 60000,
    rpId: location.hostname,
    allowCredentials: [{ type: 'public-key', id: idBytes }],
    userVerification: 'required',
  };
  let assertion;
  try {
    assertion = await navigator.credentials.get({ publicKey: { ...base, extensions: { prf: { evalByCredential } } } });
  } catch (firstError) {
    // Quelques implémentations acceptent encore la forme `eval` sur get().
    // On tente ce fallback uniquement localement ; aucune donnée n'est envoyée.
    try {
      assertion = await navigator.credentials.get({ publicKey: { ...base, extensions: { prf: { eval: { first: saltBytes } } } } });
    } catch (_) { throw firstError; }
  }
  const secret = prfResult(assertion);
  if (!secret?.length) throw new Error('Cette authentification système ne fournit pas WebAuthn PRF sur cet appareil.');
  return secret;
}

export async function registerVaultBiometric(identityPseudo = 'utilisateur') {
  if (!(await platformAuthenticatorAvailable())) throw new Error('Authentification biométrique / système WebAuthn indisponible ici. Le code du coffre reste utilisable.');
  const prfSalt = randomBytes(32);
  const pseudo = String(identityPseudo || 'utilisateur').slice(0, 64);
  const credential = await navigator.credentials.create({
    publicKey: {
      challenge: randomBytes(32),
      rp: { name: 'PatateBrûlée', id: location.hostname },
      user: {
        id: randomBytes(32),
        name: `coffre-${pseudo}`,
        displayName: `Coffre PatateBrûlée · ${pseudo}`,
      },
      pubKeyCredParams: [
        { type: 'public-key', alg: -7 },   // ES256
        { type: 'public-key', alg: -257 }, // RS256 fallback
      ],
      timeout: 60000,
      attestation: 'none',
      authenticatorSelection: {
        authenticatorAttachment: 'platform',
        residentKey: 'preferred',
        userVerification: 'required',
      },
      extensions: { prf: { eval: { first: prfSalt } } },
    },
  });
  if (!credential?.rawId) throw new Error('Credential WebAuthn non créée.');
  const credentialId = bytesToB64url(new Uint8Array(credential.rawId));
  let secret = prfResult(credential);
  if (!secret?.length) secret = await evaluateCredentialPrf(credentialId, bytesToB64url(prfSalt));
  return {
    credentialId,
    prfSalt: bytesToB64url(prfSalt),
    prfSecret: secret,
    createdAt: Date.now(),
  };
}

export async function unlockVaultBiometric(config) {
  if (!config?.credentialId || !config?.prfSalt) throw new Error('Déverrouillage biométrique non configuré.');
  if (!(await platformAuthenticatorAvailable())) throw new Error('Authentification système indisponible sur cet appareil.');
  return evaluateCredentialPrf(String(config.credentialId), String(config.prfSalt));
}

// Helpers purs pour tests.
export const __test = { bytesToB64url, b64urlToBytes };
